import { Field } from "@/components/ui/field";
import type { RunSetting } from "@/features/agents/types";
import { settingValue } from "./run-package";
import { SpecDescription } from "@/features/agents/spec-description";
import { Select } from "@/components/ui/select";

export function SettingLabel({ setting }: { setting: RunSetting }) {
  return (
    <>
      {setting.label}
      {setting.required && <span className="text-err"> (required)</span>}
    </>
  );
}

/** The setting's own description, else where the connector puts it in its request. */
export function settingHint(setting: RunSetting) {
  if (setting.description)
    return <SpecDescription text={setting.description} source={setting.description_source} />;
  return setting.target ? <span className="font-mono text-[11px]">{setting.target}</span> : undefined;
}

export function RunSettingsSection({
  settings,
  values,
  onChange,
}: {
  settings: RunSetting[];
  values: Record<string, string>;
  onChange: (key: string, value: string) => void;
}) {
  if (settings.length === 0) return null;
  return (
    <fieldset className="flex flex-col gap-3 rounded-[9px] border border-line p-3.5">
      <legend className="px-1 text-[13px] font-semibold">Run settings</legend>
      <p className="-mt-1 text-xs text-muted">The same for every item in this run.</p>
      {settings.map((s) => {
        const id = `ne-rs-${s.key}`;
        const value = settingValue(s, values);
        return (
          <Field key={s.key} label={<SettingLabel setting={s} />} htmlFor={id} hint={settingHint(s)}>
            {s.kind === "select" ? (
              <Select
                id={id}
                value={value}
                onChange={(v) => onChange(s.key, v)}
                placeholder="Pick one"
                options={[
                  ...(value && !s.options.some((o) => o.value === value) ? [{ value, label: value }] : []),
                  ...s.options.map((o) => ({ value: o.value, label: o.label })),
                ]}
              />
            ) : (
              <input
                id={id}
                className="inp w-full"
                value={value}
                required={s.required}
                onChange={(e) => onChange(s.key, e.target.value)}
              />
            )}
          </Field>
        );
      })}
    </fieldset>
  );
}
